import { ListItemButton, ListItemButtonProps, ListItemText, styled } from '@mui/material';

import type { AppLinkOptions } from '../../routing';
import { linkable } from '../../routing/links/linkable';
import { asLinkProps } from '../../routing/links/linkOptions';
import { useIsAtSecondRoute } from '../../routing/links/useIsRouteActive';
import { colorSystem } from '../../theme';

const StyledListItemButton = styled(ListItemButton)({
    color: colorSystem.neutral[7],
    background: colorSystem.neutral[0],
    '&.Mui-selected': {
        color: colorSystem.primary[7],
        borderLeft: 'transparent',
        background: colorSystem.primary[2],
    },
    '&:hover': {
        color: colorSystem.neutral[9],
        background: colorSystem.neutral.white,
    },
    '&.Mui-selected:hover': {
        color: colorSystem.primary[7],
        borderLeft: 'transparent',
        background: colorSystem.primary[2],
    },
});

const LinkListItemButton = linkable(StyledListItemButton);

export type NavListItemProps = {
    label: string | JSX.Element;
    /** Build it with `linkOptions()`. A link navigates by itself, so it takes no `onClick`. */
    link: AppLinkOptions;
} & Omit<ListItemButtonProps, 'href' | 'onClick'>;

/** A sidebar link, selected on its own route only, not on a child route. */
export function NavListItem({ label, link, ...listItemProps }: NavListItemProps): JSX.Element {
    // The router marks the link at its own URL. A page with a second route (`movedPages`) is at another one too.
    const isAtSecondRoute = useIsAtSecondRoute(link);
    return (
        <LinkListItemButton
            disableRipple
            {...listItemProps}
            {...asLinkProps(link)}
            {...(isAtSecondRoute && { selected: true, 'aria-current': 'page' as const })}
            activeProps={{ selected: true }}
            activeOptions={{ exact: true, includeSearch: false }}
        >
            <ListItemText
                primary={label}
                slotProps={{
                    primary: {
                        noWrap: true,
                    },
                }}
            />
        </LinkListItemButton>
    );
}
